import React, { useEffect, useState } from 'react';
import { MaterialStructureInfo } from './types';
import { loadRDKit } from './rdkitLoader';
import { KetcherModal } from './KetcherModal';

interface StructureEditorProps {
  value?: string;               // molfile (V2000/V3000) or SMILES
  format?: 'mol' | 'smiles';
  onChange?: (structure: MaterialStructureInfo) => void;
  onClose?: () => void;
}

/**
 * StructureEditor Component
 * 
 * Phase 4: Chemical structure editor with RDKit.js integration
 * 
 * Features:
 * - Loads RDKit.js WASM for structure parsing
 * - Auto-calculates molecular weight, formula, SMILES, InChI Key
 * - Parses formula to element counts
 * - Integrates with KetcherModal for structure drawing
 */
export const StructureEditor: React.FC<StructureEditorProps> = ({
  value,
  format = 'smiles',
  onChange,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [structure, setStructure] = useState<MaterialStructureInfo | null>(null);
  const [isKetcherOpen, setIsKetcherOpen] = useState(false);

  // Load RDKit and parse initial structure
  useEffect(() => {
    let cancelled = false;

    const loadAndParse = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const RDKit = await loadRDKit();

        if (cancelled) return;

        if (value) {
          // Parse structure
          let mol;
          try {
            if (format === 'mol') {
              mol = RDKit.get_mol(value);
            } else {
              mol = RDKit.get_mol(value);
            }

            if (!mol) {
              throw new Error('Failed to parse structure');
            }

            // Extract properties
            const smiles = mol.get_smiles();
            const inchi = mol.get_inchi();

            // Get descriptors (contains MW, formula, etc.)
            const descriptorsJson = mol.get_descriptors();
            let descriptors: any = {};
            try {
              descriptors = JSON.parse(descriptorsJson);
              // Log descriptors structure for debugging (remove in production)
              console.log('RDKit descriptors:', descriptors);
            } catch (e) {
              console.warn('Failed to parse descriptors:', e);
            }

            // Extract molecular weight from descriptors
            // RDKit.js uses 'exactmw' for exact molecular weight and 'amw' for average
            const mw = descriptors.exactmw || descriptors.amw || null;

            // Extract formula from InChI string
            // InChI format: InChI=1S/C2H6O/c1-2-3/h3H,2H2,1H3
            // Formula is in the second part after "1S/" or "1/"
            let formula: string | null = null;
            if (inchi) {
              try {
                // Parse InChI: format is InChI=VERSION/FORMULA/...
                const inchiParts = inchi.split('/');
                if (inchiParts.length > 1) {
                  // Formula is in the second part (index 1)
                  // It contains element symbols and numbers (e.g., "C2H6O")
                  const formulaPart = inchiParts[1];
                  // Extract formula: match element symbols (1-2 letters) followed by optional numbers
                  // Pattern matches: C, C2, H6, O, etc.
                  const formulaMatch = formulaPart.match(/^([A-Z][a-z]?\d*)+/);
                  if (formulaMatch) {
                    formula = formulaMatch[0];
                  }
                }
              } catch (e) {
                console.warn('Failed to extract formula from InChI:', e, 'InChI:', inchi);
              }
            }

            // Debug: Log what we found
            console.log('Extracted structure data:', {
              mw,
              formula,
              inchi,
              descriptorsKeys: Object.keys(descriptors).slice(0, 10),
            });

            // Get InChI Key from InChI string using RDKit module method
            let inchiKey: string | null = null;
            try {
              if (inchi && RDKit.get_inchikey_for_inchi) {
                inchiKey = RDKit.get_inchikey_for_inchi(inchi);
              }
            } catch (e) {
              console.warn('Failed to get InChI Key:', e);
            }

            // Parse formula to element counts (only if formula exists)
            const elementCounts = formula ? parseFormula(formula) : undefined;

            const structureInfo: MaterialStructureInfo = {
              molfile: format === 'mol' ? value : undefined,
              smiles,
              inchiKey,
              formula,
              molWeight: mw,
              elementCounts,
            };

            setStructure(structureInfo);
          } catch (parseError) {
            console.error('Structure parsing error:', parseError);
            setError(`Invalid structure: ${parseError}`);
          }
        }

        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('RDKit loading error:', err);
        setError(`Failed to load RDKit: ${err}`);
        setIsLoading(false);
      }
    };

    loadAndParse();

    return () => {
      cancelled = true;
    };
  }, [value, format]);

  /**
   * Parse formula string to element counts
   * Example: "C6H6O" -> { C: 6, H: 6, O: 1 }
   */
  const parseFormula = (formula: string): Record<string, number> => {
    const counts: Record<string, number> = {};

    // Match element symbols followed by optional numbers
    // Pattern: ([A-Z][a-z]?)(\d*)
    const regex = /([A-Z][a-z]?)(\d*)/g;
    let match;

    while ((match = regex.exec(formula)) !== null) {
      const element = match[1];
      const count = match[2] ? parseInt(match[2], 10) : 1;
      counts[element] = (counts[element] || 0) + count;
    }

    return counts;
  };

  /**
   * Handle structure save from Ketcher
   */
  const handleStructureSave = async (smiles: string, molfile: string) => {
    try {
      const RDKit = await loadRDKit();
      const mol = RDKit.get_mol(smiles || molfile);

      if (!mol) {
        throw new Error('Failed to parse structure from Ketcher');
      }

      const parsedSmiles = mol.get_smiles();
      const inchi = mol.get_inchi();

      // Get descriptors (contains MW, formula, etc.)
      const descriptorsJson = mol.get_descriptors();
      let descriptors: any = {};
      try {
        descriptors = JSON.parse(descriptorsJson);
      } catch (e) {
        console.warn('Failed to parse descriptors:', e);
      }

      // Extract molecular weight from descriptors (RDKit.js uses 'exactmw' or 'amw')
      const mw = descriptors.exactmw || descriptors.amw || null;

      // Extract formula from InChI string
      // InChI format: InChI=1S/C2H6O/c1-2-3/h3H,2H2,1H3
      let formula: string | null = null;
      if (inchi) {
        try {
          const inchiParts = inchi.split('/');
          if (inchiParts.length > 1) {
            const formulaPart = inchiParts[1];
            // Extract formula: match element symbols followed by optional numbers
            const formulaMatch = formulaPart.match(/^([A-Z][a-z]?\d*)+/);
            if (formulaMatch) {
              formula = formulaMatch[0];
            }
          }
        } catch (e) {
          console.warn('Failed to extract formula from InChI:', e, 'InChI:', inchi);
        }
      }

      // Get InChI Key from InChI string using RDKit module method
      let inchiKey: string | null = null;
      try {
        if (inchi && RDKit.get_inchikey_for_inchi) {
          inchiKey = RDKit.get_inchikey_for_inchi(inchi);
        }
      } catch (e) {
        console.warn('Failed to get InChI Key:', e);
      }

      const elementCounts = formula ? parseFormula(formula) : undefined;

      const structureInfo: MaterialStructureInfo = {
        molfile: molfile || undefined,
        smiles: parsedSmiles,
        inchiKey,
        formula,
        molWeight: mw,
        elementCounts,
      };

      setStructure(structureInfo);
      onChange?.(structureInfo);
    } catch (err) {
      console.error('Failed to process structure from Ketcher:', err);
      setError(`Failed to process structure: ${err}`);
    }
  };

  /**
   * Handle manual import
   */
  const handleImport = async (importValue: string, isMolfile: boolean) => {
    try {
      const RDKit = await loadRDKit();
      const mol = RDKit.get_mol(importValue);

      if (!mol) {
        throw new Error('Failed to parse imported structure');
      }

      const smiles = mol.get_smiles();
      const inchi = mol.get_inchi();

      // Get descriptors (contains MW, etc.)
      const descriptorsJson = mol.get_descriptors();
      let descriptors: any = {};
      try {
        descriptors = JSON.parse(descriptorsJson);
      } catch (e) {
        console.warn('Failed to parse descriptors:', e);
      }

      // Extract molecular weight from descriptors (RDKit.js uses 'exactmw' or 'amw')
      const mw = descriptors.exactmw || descriptors.amw || null;

      // Extract formula from InChI string
      // InChI format: InChI=1S/C2H6O/c1-2-3/h3H,2H2,1H3
      let formula: string | null = null;
      if (inchi) {
        try {
          const inchiParts = inchi.split('/');
          if (inchiParts.length > 1) {
            const formulaPart = inchiParts[1];
            // Extract formula: match element symbols followed by optional numbers
            const formulaMatch = formulaPart.match(/^([A-Z][a-z]?\d*)+/);
            if (formulaMatch) {
              formula = formulaMatch[0];
            }
          }
        } catch (e) {
          console.warn('Failed to extract formula from InChI:', e, 'InChI:', inchi);
        }
      }

      // Get InChI Key from InChI string using RDKit module method
      let inchiKey: string | null = null;
      try {
        if (inchi && RDKit.get_inchikey_for_inchi) {
          inchiKey = RDKit.get_inchikey_for_inchi(inchi);
        }
      } catch (e) {
        console.warn('Failed to get InChI Key:', e);
      }

      const elementCounts = formula ? parseFormula(formula) : undefined;

      const structureInfo: MaterialStructureInfo = {
        molfile: isMolfile ? importValue : undefined,
        smiles,
        inchiKey,
        formula,
        molWeight: mw,
        elementCounts,
      };

      setStructure(structureInfo);
      onChange?.(structureInfo);
      setError(null);
    } catch (err) {
      console.error('Import error:', err);
      setError(`Failed to import structure: ${err}`);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading structure editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {error && (
        <div style={styles.errorBox}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Structure Display */}
      {structure && (
        <div style={styles.structureInfo}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Molecular Weight:</span>
            <span style={styles.infoValue}>{structure.molWeight?.toFixed(2)} g/mol</span>
          </div>
          {structure.formula && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Formula:</span>
              <span style={styles.infoValue}>{structure.formula}</span>
            </div>
          )}
          {structure.smiles && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>SMILES:</span>
              <code style={styles.smilesCode}>{structure.smiles}</code>
            </div>
          )}
          {structure.elementCounts && Object.keys(structure.elementCounts).length > 0 && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Composition:</span>
              <span style={styles.infoValue}>
                {Object.entries(structure.elementCounts)
                  .map(([el, count]) => `${el}${count > 1 ? count : ''}`)
                  .join(' ')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={styles.actions}>
        <button
          style={styles.button}
          onClick={() => setIsKetcherOpen(true)}
        >
          {structure ? 'Edit Structure' : 'Draw Structure'}
        </button>
        <button
          style={styles.buttonSecondary}
          onClick={() => {
            const importValue = prompt('Paste SMILES or MOLfile:');
            if (importValue) {
              const isMolfile = importValue.includes('M  END') || importValue.split('\n').length > 5;
              handleImport(importValue.trim(), isMolfile);
            }
          }}
        >
          Import
        </button>
        {onClose && (
          <button
            style={styles.buttonSecondary}
            onClick={onClose}
          >
            Close
          </button>
        )}
      </div>

      {/* Ketcher Modal */}
      <KetcherModal
        isOpen={isKetcherOpen}
        onClose={() => setIsKetcherOpen(false)}
        onSave={handleStructureSave}
        initialStructure={structure?.smiles || structure?.molfile}
      />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '16px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e2e8f0',
    borderTop: '3px solid #2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '12px',
    fontSize: '14px',
    color: '#64748b',
  },
  errorBox: {
    padding: '12px',
    marginBottom: '16px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#991b1b',
    fontSize: '13px',
  },
  structureInfo: {
    padding: '12px',
    marginBottom: '16px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  infoLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#64748b',
  },
  infoValue: {
    fontSize: '13px',
    color: '#1e293b',
    fontWeight: 500,
  },
  smilesCode: {
    fontSize: '12px',
    fontFamily: 'monospace',
    backgroundColor: '#fff',
    padding: '2px 6px',
    borderRadius: '4px',
    color: '#1e293b',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  button: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  buttonSecondary: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};

// Add spinner animation
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('structure-editor-spinner-style');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'structure-editor-spinner-style';
    styleSheet.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}

